import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Wand2, Feather } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { InkGenerationOverlay } from './ink-generation';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalFooter,
} from './ui/responsive-modal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { cn } from '../lib/utils';
import { useAppStore } from '../stores/app-store';
import { useAIQualityStore, AI_MODEL_MAP } from '../stores/ai-quality-store';
import { api, AiLimitError, type BibleEntry } from '../lib/api';
import { notify } from '../lib/toast';
import { BODY_TYPES, type SectionType, type Chapter } from '../types';

// pt-BR labels matching the ones defined in app-shell.tsx
const BODY_SECTION_LABELS: Record<string, string> = {
  part: 'Parte',
  prologue: 'Prólogo',
  chapter: 'Capítulo',
  interlude: 'Interlúdio',
  epilogue: 'Epílogo',
};

// Maps each section type to its i18n key.
const BODY_SECTION_KEYS: Record<string, string> = {
  part: 'sectionPart',
  prologue: 'sectionPrologue',
  chapter: 'sectionChapter',
  interlude: 'sectionInterlude',
  epilogue: 'sectionEpilogue',
};

type SizeOption = 'short' | 'medium' | 'long';

const SIZE_LABEL: Record<SizeOption, string> = {
  short: 'Curto',
  medium: 'Médio',
  long: 'Longo',
};
const SIZE_HINT: Record<SizeOption, string> = {
  short: '~500 pal.',
  medium: '~900 pal.',
  long: '~1500 pal.',
};

// Maps each size option to its i18n keys.
const SIZE_LABEL_KEYS: Record<SizeOption, string> = {
  short: 'sizeShortLabel',
  medium: 'sizeMediumLabel',
  long: 'sizeLongLabel',
};
const SIZE_HINT_KEYS: Record<SizeOption, string> = {
  short: 'sizeShortHint',
  medium: 'sizeMediumHint',
  long: 'sizeLongHint',
};
const SIZE_WORDS: Record<SizeOption, number> = {
  short: 500,
  medium: 900,
  long: 1500,
};

/**
 * Remove a first line that merely echoes the chapter title or is a bare
 * "Capítulo N" / "Chapter N" / markdown heading — so the prose doesn't start
 * by repeating the title. Conservative: only strips obvious heading lines.
 */
function stripLeadingTitle(text: string, title: string): string {
  const lines = text.split('\n');
  const first = (lines[0] ?? '').trim();
  if (!first) return text;
  const norm = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
  const isEcho = first.length <= 80 && norm(first) === norm(title);
  const isBareHeading = /^(cap[ií]tulo|chapter|parte|part|pr[óo]logo|ep[íi]logo)\s*\d+\s*$/i.test(first);
  const isMarkdownHeading = /^#{1,3}\s+\S/.test(first);
  if (isEcho || isBareHeading || isMarkdownHeading) {
    return lines.slice(1).join('\n').trim();
  }
  return text;
}

interface NewChapterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  /** All current chapters — used for auto-numbering and continuity context. */
  chapters: Chapter[];
  /** Called with the new chapter id after successful creation. */
  onCreated?: (chapterId: string) => void;
}

export function NewChapterModal({
  open,
  onOpenChange,
  projectId,
  chapters,
  onCreated,
}: NewChapterModalProps) {
  const [tab, setTab] = useState<'empty' | 'ai'>('empty');
  const [sectionType, setSectionType] = useState<SectionType>('chapter');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);

  // AI-tab specific state
  const [prompt, setPrompt] = useState('');
  const [selectedCharacters, setSelectedCharacters] = useState<string[]>([]);
  const [fromScratch, setFromScratch] = useState(false);
  const [size, setSize] = useState<SizeOption>('medium');
  const [characters, setCharacters] = useState<BibleEntry[]>([]);
  const [charQuery, setCharQuery] = useState('');
  const [loadingChars, setLoadingChars] = useState(false);

  // Ink generation overlay: genText null = bloom (generating), string = reveal.
  const [genOpen, setGenOpen] = useState(false);
  const [genText, setGenText] = useState<string | null>(null);
  const pendingRef = useRef<
    | { html: string; finalTitle: string; sectionType: SectionType; body: string; existingNames: string[] }
    | null
  >(null);

  const { addChapter } = useAppStore();
  const { quality } = useAIQualityStore();
  const { t } = useTranslation();

  // Load characters when the AI tab is visible and modal is open
  useEffect(() => {
    if (!open || tab !== 'ai' || !projectId) return;
    setLoadingChars(true);
    api
      .getBibleEntries(projectId)
      .then((res) => setCharacters(res.entries.filter((e) => e.type === 'character')))
      .catch(() => setCharacters([]))
      .finally(() => setLoadingChars(false));
  }, [open, tab, projectId]);

  // Reset all state when the modal closes
  useEffect(() => {
    if (!open) {
      setTab('empty');
      setSectionType('chapter');
      setTitle('');
      setPrompt('');
      setSelectedCharacters([]);
      setFromScratch(false);
      setSize('medium');
      setCharacters([]);
      setCharQuery('');
    }
  }, [open]);

  // Auto-generate title consistent with handleAddBodyChapter in app-shell.tsx
  const getEffectiveTitle = (): string => {
    if (title.trim()) return title.trim();
    if (sectionType === 'chapter') {
      const chapterCount = chapters.filter((c) => c.sectionType === 'chapter').length;
      return t('newChapter.autoChapterTitle', { number: chapterCount + 1, defaultValue: 'Capítulo {{number}}' });
    }
    return t(`newChapter.${BODY_SECTION_KEYS[sectionType] ?? sectionType}`, BODY_SECTION_LABELS[sectionType] ?? sectionType);
  };

  const toggleCharacter = (name: string) => {
    setSelectedCharacters((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  // Create an empty chapter and navigate to it
  const handleCreateEmpty = async () => {
    setLoading(true);
    try {
      const effectiveTitle = getEffectiveTitle();
      const chapterId = await addChapter(projectId, effectiveTitle, sectionType, '');
      onCreated?.(chapterId);
      onOpenChange(false);
    } catch {
      notify.error(t('newChapter.toastCreateErrorTitle', 'Erro ao criar capítulo'), t('newChapter.tryAgain', 'Tente novamente.'));
    } finally {
      setLoading(false);
    }
  };

  // Generate the prose, play the ink "writing" overlay (bloom while the model
  // works → the text writes itself), then create the chapter when the reveal
  // finishes. Values are stashed in pendingRef because closing the modal resets
  // the form state before the reveal completes.
  const handleCreateWithAI = async () => {
    setLoading(true);
    setGenText(null);
    setGenOpen(true);      // bloom phase starts immediately
    onOpenChange(false);   // hide the form — the overlay takes over
    try {
      const userTitle = title.trim();
      const fallbackTitle = getEffectiveTitle();
      const capturedSection = sectionType;

      // Build the outline string (continuity)
      let outline: string;
      if (fromScratch) {
        outline = prompt.trim() || (userTitle || fallbackTitle);
      } else {
        const precedingTitles = chapters.map((c) => c.title);
        const parts: string[] = [
          'Continue a história de forma criativa e consistente.',
          precedingTitles.length > 0
            ? `Capítulos anteriores: ${precedingTitles.join(', ')}.`
            : '',
          prompt.trim(),
        ].filter(Boolean);
        outline = parts.join(' ');
      }
      if (selectedCharacters.length > 0) {
        outline += ` Personagens em foco: ${selectedCharacters.join(', ')}.`;
      }

      // 1) Generate the draft.
      const res = await api.generateDraft(
        outline,
        userTitle || fallbackTitle,
        SIZE_WORDS[size],
        undefined,
        projectId,
        AI_MODEL_MAP[quality],
      );

      if (!res.draft || !res.draft.trim()) {
        setGenOpen(false);
        notify.error(t('newChapter.toastNoContentTitle', 'A IA não retornou conteúdo'), t('newChapter.toastNoContentDescription', 'Tente de novo ou ajuste as instruções/tamanho.'));
        return;
      }

      // 2) Strip any echoed title/heading line, then convert to <p> HTML.
      const body = stripLeadingTitle(res.draft.trim(), userTitle || fallbackTitle);
      const html = body
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => `<p>${p.replace(/</g, '&lt;').replace(/\n/g, ' ')}</p>`)
        .join('');

      // 3) Title: the user's, else an AI-suggested one (styled to the book's format).
      let finalTitle = userTitle;
      if (!finalTitle) {
        try {
          const siblingTitles = chapters.map((c) => c.title).filter(Boolean);
          const r = await api.suggestTitle(body, projectId, siblingTitles);
          finalTitle = r.title?.trim() || fallbackTitle;
        } catch {
          finalTitle = fallbackTitle;
        }
      }

      // 4) Stash and trigger the REVEAL — the chapter is created when it ends.
      pendingRef.current = {
        html,
        finalTitle,
        sectionType: capturedSection,
        body,
        existingNames: characters.map((c) => c.name),
      };
      setGenText(body);
    } catch (err) {
      setGenOpen(false);
      pendingRef.current = null;
      if (err instanceof AiLimitError) {
        notify.aiLimitReached(err.type);
      } else {
        notify.error(t('newChapter.toastGenerateErrorTitle', 'Erro ao gerar capítulo'), t('newChapter.tryAgain', 'Tente novamente.'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Called when the ink reveal finishes (or the user taps to skip it): create
  // the chapter from the stashed data and hand off to the editor.
  const finishGeneration = useCallback(async () => {
    const p = pendingRef.current;
    pendingRef.current = null;
    setGenOpen(false);
    setGenText(null);
    if (!p) return; // skipped during bloom / error — nothing to create
    try {
      const chapterId = await addChapter(projectId, p.finalTitle, p.sectionType, p.html);
      onCreated?.(chapterId);
      // Non-blocking: add any NEW named characters to the Story Bible.
      void (async () => {
        try {
          const extracted = await api.extractCharacters(p.body, p.existingNames, projectId);
          let created = 0;
          for (const char of extracted.characters) {
            try {
              await api.createBibleEntry(projectId, { type: 'character', name: char.name, content: char.description });
              created++;
            } catch {
              /* best-effort */
            }
          }
          if (created > 0) {
            notify.success(
              created > 1
                ? t('newChapter.toastCharactersAddedPlural', { count: created, defaultValue: '{{count}} personagens adicionados ao Universo' })
                : t('newChapter.toastCharactersAddedSingular', { count: created, defaultValue: '{{count}} personagem adicionado ao Universo' })
            );
          }
        } catch {
          /* never break the flow */
        }
      })();
    } catch {
      notify.error(t('newChapter.toastCreateErrorTitle', 'Erro ao criar capítulo'), t('newChapter.tryAgain', 'Tente novamente.'));
    }
  }, [projectId, addChapter, onCreated, t]);

  return (
    <>
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent className="sm:max-w-[540px] overflow-hidden">
        <ResponsiveModalHeader className="space-y-1">
          <p className="m-eyebrow">{t('newChapter.title', 'Novo capítulo')}</p>
          <ResponsiveModalTitle className="font-serif text-[21px] font-medium leading-snug">
            {t('newChapter.subtitle', 'Para onde a história vai agora?')}
          </ResponsiveModalTitle>
        </ResponsiveModalHeader>

        {/* Vazio <-> Com IA: pílula deslizante da casca */}
        <div className="relative flex gap-1 rounded-[10px] bg-foreground/5 p-[3px]">
          {([
            { v: 'empty' as const, label: t('newChapter.tabEmpty', 'Vazio'), Icon: Feather },
            { v: 'ai' as const, label: t('newChapter.tabAi', 'Com IA'), Icon: Wand2 },
          ]).map(({ v, label, Icon }) => (
            <button
              key={v}
              type="button"
              onClick={() => setTab(v)}
              className={cn(
                'relative flex-1 px-3 py-2.5 rounded-lg font-mono text-[10px] uppercase tracking-[0.18em] transition-colors touch-manipulation',
                tab === v ? 'text-[color:var(--ember-hot)]' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab === v && (
                <motion.span
                  layoutId="nc-tab-pill"
                  className="absolute inset-0 rounded-lg bg-[rgba(223,201,147,0.08)] shadow-[0_0_0_1px_rgba(223,201,147,0.28)_inset]"
                  transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                />
              )}
              <span className="relative z-10 inline-flex items-center gap-1.5">
                <Icon size={11} /> {label}
              </span>
            </button>
          ))}
        </div>

        {/* Campos compartilhados */}
        <div className="space-y-3.5">
          <div className="space-y-1.5">
            <Label className="sh-l !m-0">{t('newChapter.sectionTypeLabel', 'Tipo de seção')}</Label>
            <Select
              value={sectionType}
              onValueChange={(v) => setSectionType(v as SectionType)}
            >
              <SelectTrigger className="bg-card border-0 ring-1 ring-inset ring-border rounded-[11px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BODY_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`newChapter.${BODY_SECTION_KEYS[type] ?? type}`, BODY_SECTION_LABELS[type] ?? type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="sh-l !m-0">
              {t('newChapter.titleLabel', 'Título')}{' '}
              <span className="normal-case tracking-normal opacity-70">{t('newChapter.optional', '(opcional)')}</span>
            </Label>
            <Input
              placeholder={getEffectiveTitle()}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-card border-0 ring-1 ring-inset ring-border rounded-[11px] font-serif"
            />
          </div>
        </div>

        {/* Conteúdo da aba: entra/sai animado, seamless */}
        <AnimatePresence mode="wait" initial={false}>
          {tab === 'empty' ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <ResponsiveModalFooter className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  disabled={loading}
                  className="px-4 py-2.5 rounded-[9px] font-mono text-[9.5px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground bg-foreground/5 ring-1 ring-inset ring-border transition-colors disabled:opacity-50"
                >
                  {t('newChapter.cancel', 'Cancelar')}
                </button>
                <button type="button" onClick={handleCreateEmpty} disabled={loading} className="btn-ember">
                  {loading ? <Loader2 size={13} className="animate-spin" /> : <Feather size={12} />}
                  {t('newChapter.createChapter', 'Criar capítulo')}
                </button>
              </ResponsiveModalFooter>
            </motion.div>
          ) : (
            <motion.div
              key="ai"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="space-y-4"
            >
              {/* Instruções livres */}
              <div className="space-y-1.5">
                <Label className="sh-l !m-0">
                  {t('newChapter.instructionsLabel', 'Instruções')}{' '}
                  <span className="normal-case tracking-normal opacity-70">{t('newChapter.optional', '(opcional)')}</span>
                </Label>
                <Textarea
                  placeholder={t('newChapter.instructionsPlaceholder', 'O que deve acontecer neste capítulo? Descreva a cena, conflito, revelação…')}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={3}
                  className="resize-none bg-card border-0 ring-1 ring-inset ring-border rounded-[11px]"
                />
              </div>

              {/* Personagens do Universo */}
              <div className="space-y-1.5">
                <Label className="sh-l !m-0">{t('newChapter.charactersLabel', 'Personagens')}</Label>
                {loadingChars ? (
                  <div className="flex items-center gap-2 py-1 text-sm text-muted-foreground">
                    <Loader2 size={14} className="animate-spin" />
                    {t('newChapter.loadingCharacters', 'Carregando personagens…')}
                  </div>
                ) : characters.length === 0 ? (
                  <p className="text-xs italic text-muted-foreground">
                    {t('newChapter.noCharacters', 'Nenhum personagem no Universo do Livro ainda.')}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {characters.length > 6 && (
                      <Input
                        value={charQuery}
                        onChange={(e) => setCharQuery(e.target.value)}
                        placeholder={t('newChapter.searchCharacters', 'Buscar personagem…')}
                        className="h-8 bg-card border-0 ring-1 ring-inset ring-border rounded-[9px] text-[12px]"
                      />
                    )}
                    {/* uma fileira só, rolando na horizontal */}
                    <div className="flex flex-nowrap gap-1.5 overflow-x-auto no-scrollbar pb-1">
                      {characters
                        .filter((c) => !charQuery.trim() || c.name.toLowerCase().includes(charQuery.trim().toLowerCase()))
                        .map((char) => (
                          <button
                            key={char.id}
                            type="button"
                            onClick={() => toggleCharacter(char.name)}
                            className={cn(
                              'inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-[12px] transition-all touch-manipulation max-w-[150px] truncate ring-1 ring-inset',
                              selectedCharacters.includes(char.name)
                                ? 'bg-[rgba(223,201,147,0.1)] text-[color:var(--ember-hot)] ring-[rgba(223,201,147,0.4)]'
                                : 'bg-foreground/5 text-muted-foreground ring-border hover:text-foreground'
                            )}
                          >
                            {selectedCharacters.includes(char.name) && <span className="text-[9px]">✦</span>}
                            <span className="truncate">{char.name}</span>
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Base da geração */}
              <div className="space-y-1.5">
                <Label className="sh-l !m-0">{t('newChapter.generationBaseLabel', 'Base da geração')}</Label>
                <div className="flex gap-1.5">
                  {([
                    { v: false, label: t('newChapter.continueStory', 'Continuar a história') },
                    { v: true, label: t('newChapter.fromScratch', 'Do zero') },
                  ]).map(({ v, label }) => (
                    <button
                      key={String(v)}
                      type="button"
                      onClick={() => setFromScratch(v)}
                      className={cn(
                        'flex-1 rounded-[10px] px-3 py-2.5 text-[12px] transition-all touch-manipulation ring-1 ring-inset',
                        fromScratch === v
                          ? 'bg-[rgba(223,201,147,0.08)] text-[color:var(--ember-hot)] ring-[rgba(223,201,147,0.35)]'
                          : 'bg-foreground/5 text-muted-foreground ring-border hover:text-foreground'
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <motion.p
                  key={String(fromScratch)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.18 }}
                  className="text-[11px] leading-relaxed text-muted-foreground"
                >
                  {fromScratch
                    ? t('newChapter.fromScratchHint', 'Escreve apenas a partir das instruções, ignorando o contexto existente.')
                    : t('newChapter.continueStoryHint', 'Usa o Universo do Livro e capítulos anteriores para manter consistência.')}
                </motion.p>
              </div>

              {/* Tamanho */}
              <div className="space-y-1.5">
                <Label className="sh-l !m-0">{t('newChapter.sizeLabel', 'Tamanho')}</Label>
                <div className="flex gap-1.5">
                  {(['short', 'medium', 'long'] as const).map((sz) => (
                    <button
                      key={sz}
                      type="button"
                      onClick={() => setSize(sz)}
                      className={cn(
                        'flex flex-1 flex-col items-center gap-0.5 rounded-[10px] px-2 py-2.5 transition-all touch-manipulation ring-1 ring-inset',
                        size === sz
                          ? 'bg-[rgba(223,201,147,0.08)] ring-[rgba(223,201,147,0.35)]'
                          : 'bg-foreground/5 ring-border'
                      )}
                    >
                      <span className={cn('text-[12px]', size === sz ? 'text-[color:var(--ember-hot)]' : 'text-muted-foreground')}>
                        {t(`newChapter.${SIZE_LABEL_KEYS[sz]}`, SIZE_LABEL[sz])}
                      </span>
                      <span className="font-mono text-[8px] uppercase tracking-[0.1em] text-muted-foreground/70">
                        {t(`newChapter.${SIZE_HINT_KEYS[sz]}`, SIZE_HINT[sz])}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <ResponsiveModalFooter className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  disabled={loading}
                  className="px-4 py-2.5 rounded-[9px] font-mono text-[9.5px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground bg-foreground/5 ring-1 ring-inset ring-border transition-colors disabled:opacity-50"
                >
                  {t('newChapter.cancel', 'Cancelar')}
                </button>
                <button type="button" onClick={handleCreateWithAI} disabled={loading} className="btn-ember">
                  {loading ? (
                    <>
                      <Loader2 size={13} className="animate-spin" />
                      {t('newChapter.creatingAndGenerating', 'Criando e gerando…')}
                    </>
                  ) : (
                    <>
                      ✦ {t('newChapter.createAndGenerate', 'Criar e gerar')}
                    </>
                  )}
                </button>
              </ResponsiveModalFooter>
            </motion.div>
          )}
        </AnimatePresence>
      </ResponsiveModalContent>
    </ResponsiveModal>

    {/* AI writing moment — ink bloom while generating, then the prose writes itself */}
    <InkGenerationOverlay
      open={genOpen}
      text={genText}
      onRevealDone={finishGeneration}
      onSkip={finishGeneration}
    />
    </>
  );
}
